import React, { useState, useEffect } from 'react';
import { formatTimestamp } from '../utils/dateUtils';
import { getStatusDisplayName, getStatusColorClass } from '../constants/statusConstants';

/**
 * RecentHistoryDialog Component
 * 
 * Displays a dialog with the most recent SharePoint file uploads/updates.
 * Shows client name, filename, status, and last updated timestamp.
 * Allows filtering to exclude "done" items or groups.
 * Supports grouping by client with simplified Done/Pending Review status.
 * Clicking a row will select that client and close the dialog.
 */
export default function RecentHistoryDialog({ isOpen, onClose, onFetchHistory, onSelectClient }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [excludeDone, setExcludeDone] = useState(false);
  const [groupByClient, setGroupByClient] = useState(false);
  const [sortField, setSortField] = useState('updatedAt'); // 'updatedAt', 'clientName', 'filename', 'status'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'

  // Fetch records when dialog opens only; filtering is done client-side
  useEffect(() => {
    if (isOpen) {
      fetchRecords();
    }
  }, [isOpen]);

  const fetchRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await onFetchHistory(false);
      setRecords(data.records || []);
    } catch (err) {
      console.error('Error fetching recent history:', err);
      setError(err.message || 'Failed to fetch recent history');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Change field and reset to descending
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const handleRowClick = (clientName) => {
    if (clientName && onSelectClient) {
      onSelectClient(clientName);
      onClose();
    }
  };

  const getSortedRecords = () => {
    const filtered = excludeDone ? records.filter((r) => r.status !== 'done') : records;
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'clientName':
          aValue = (a.clientName || '').toLowerCase();
          bValue = (b.clientName || '').toLowerCase();
          break;
        case 'filename':
          aValue = (a.acceptedName || a.suggestedName || a.originalFilename || '').toLowerCase();
          bValue = (b.acceptedName || b.suggestedName || b.originalFilename || '').toLowerCase();
          break;
        case 'status':
          aValue = a.status || '';
          bValue = b.status || '';
          break;
        case 'updatedAt':
        default:
          aValue = a.updatedAt || 0;
          bValue = b.updatedAt || 0;
          break;
      }

      if (sortField === 'updatedAt') {
        // For timestamps, compare numerically
        if (sortOrder === 'asc') {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      } else {
        // For strings, use localeCompare
        if (sortOrder === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
    });
    return sorted;
  };

  const getGroupedRecords = () => {
    const groupMap = {};
    records.forEach((record) => {
      const key = record.clientName || 'N/A';
      if (!groupMap[key]) {
        groupMap[key] = { clientName: key, records: [] };
      }
      groupMap[key].records.push(record);
    });

    const groups = Object.values(groupMap).map((group) => {
      const isDone = group.records.every((r) => r.status === 'done');
      const latestUpdatedAt = Math.max(...group.records.map((r) => r.updatedAt || 0));
      return { clientName: group.clientName, isDone, latestUpdatedAt };
    });

    const filtered = excludeDone ? groups.filter((g) => !g.isDone) : groups;

    filtered.sort((a, b) => {
      if (sortField === 'updatedAt') {
        return sortOrder === 'asc'
          ? a.latestUpdatedAt - b.latestUpdatedAt
          : b.latestUpdatedAt - a.latestUpdatedAt;
      }
      const aVal = a.clientName.toLowerCase();
      const bVal = b.clientName.toLowerCase();
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

    return filtered;
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <i className="bi bi-arrow-down-up text-muted ms-1" style={{ fontSize: '0.8em' }}></i>;
    }
    return sortOrder === 'asc' 
      ? <i className="bi bi-arrow-up ms-1" style={{ fontSize: '0.8em' }}></i>
      : <i className="bi bi-arrow-down ms-1" style={{ fontSize: '0.8em' }}></i>;
  };

  if (!isOpen) return null;

  const sortedRecords = groupByClient ? [] : getSortedRecords();
  const groupedRecords = groupByClient ? getGroupedRecords() : [];

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <div>
              <h5 className="modal-title">Recent Upload History</h5>
              <small className="text-muted">Click a row to select that client</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          
          <div className="modal-body">
            {/* Filter Controls */}
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="excludeDoneCheckbox"
                    checked={excludeDone}
                    onChange={(e) => setExcludeDone(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="excludeDoneCheckbox">
                    Hide completed {groupByClient ? 'groups' : 'items'}
                  </label>
                </div>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="groupByClientToggle"
                    checked={groupByClient}
                    onChange={(e) => setGroupByClient(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="groupByClientToggle">
                    Group by client
                  </label>
                </div>
              </div>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchRecords}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Refresh
              </button>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading recent history...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="alert alert-danger" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            )}

            {/* Records Table */}
            {!loading && !error && (
              <>
                {groupByClient ? (
                  /* Grouped View */
                  groupedRecords.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
                      <p className="mt-2">No records found</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead className="table-light">
                          <tr>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('clientName')}>
                              Client Name {getSortIcon('clientName')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                              Status {getSortIcon('status')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('updatedAt')}>
                              Last Updated {getSortIcon('updatedAt')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedRecords.map((group) => (
                            <tr
                              key={group.clientName}
                              onClick={() => handleRowClick(group.clientName)}
                              style={{ cursor: 'pointer' }}
                              className="table-row-hover"
                            >
                              <td>{group.clientName}</td>
                              <td>
                                <span className={`badge ${group.isDone ? 'bg-success' : 'bg-warning text-dark'}`}>
                                  {group.isDone ? 'Done' : 'Pending Review'}
                                </span>
                              </td>
                              <td>
                                {group.latestUpdatedAt
                                  ? formatTimestamp(group.latestUpdatedAt)
                                  : 'N/A'
                                }
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : (
                  /* Individual Records View */
                  sortedRecords.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <i className="bi bi-inbox" style={{ fontSize: '3rem' }}></i>
                      <p className="mt-2">No records found</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover table-sm">
                        <thead className="table-light">
                          <tr>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('clientName')}>
                              Client Name {getSortIcon('clientName')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('filename')}>
                              Filename {getSortIcon('filename')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('status')}>
                              Status {getSortIcon('status')}
                            </th>
                            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('updatedAt')}>
                              Last Updated {getSortIcon('updatedAt')}
                            </th>
                            <th>Uploaded By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedRecords.map((record) => {
                            const displayName = record.acceptedName || record.suggestedName || record.originalFilename || 'Unknown';
                            const statusColorClass = getStatusColorClass(record.status);
                            
                            return (
                              <tr 
                                key={record.fileId}
                                onClick={() => handleRowClick(record.clientName)}
                                style={{ cursor: 'pointer' }}
                                className="table-row-hover"
                              >
                                <td>{record.clientName || 'N/A'}</td>
                                <td>
                                  <span className="text-truncate d-inline-block" style={{ maxWidth: '300px' }} title={displayName}>
                                    {displayName}
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${statusColorClass}`}>
                                    {getStatusDisplayName(record.status)}
                                  </span>
                                </td>
                                <td>
                                  {record.updatedAt 
                                    ? formatTimestamp(record.updatedAt)
                                    : 'N/A'
                                  }
                                </td>
                                <td>{record.uploadedBy || 'N/A'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
                
                {/* Record Count */}
                {(groupByClient ? groupedRecords.length > 0 : sortedRecords.length > 0) && (
                  <div className="text-muted text-end mt-2">
                    <small>
                      {groupByClient
                        ? `Showing ${groupedRecords.length} client${groupedRecords.length !== 1 ? 's' : ''}`
                        : `Showing ${sortedRecords.length} record${sortedRecords.length !== 1 ? 's' : ''}`
                      }
                    </small>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
